import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'services/login/hooks';
import {
  useAddObjectiveCommentMutation,
  useDeleteObjectiveCommentMutation,
  useGetObjectiveCommentsQuery,
  useGetObjectiveEventsQuery,
  usePatchObjectiveCommentMutation,
} from 'services/objectives/api';
import { Objective } from 'services/objectives/types';
import { useCurrentOrganizationUsersQuery } from 'services/organizations/api';
import { useGetProjectsQuery } from 'services/projects/api';
import { assigneeDisplayName, withCurrentAssignableUser } from 'services/users/assignable';
import Discussion from 'components/comments/Discussion';
import { summarizeObjectiveEvent } from './formatObjectiveHistoryEvent';

interface ObjectiveDiscussionProps {
  objective: Objective;
}

const ObjectiveDiscussion = ({
  objective,
}: ObjectiveDiscussionProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: assignableUsers } = useCurrentOrganizationUsersQuery();
  const users = withCurrentAssignableUser(assignableUsers, user);
  const { data: comments, isLoading: commentsLoading } = useGetObjectiveCommentsQuery(objective.id, {
    skip: !objective.id,
  });
  const { data: events, isLoading: eventsLoading } = useGetObjectiveEventsQuery(objective.id, {
    skip: !objective.id,
  });
  const { data: projects } = useGetProjectsQuery();
  const [addComment] = useAddObjectiveCommentMutation();
  const [patchComment] = usePatchObjectiveCommentMutation();
  const [deleteComment] = useDeleteObjectiveCommentMutation();

  return (
    <Discussion
      comments={comments}
      currentUserId={user?.id}
      isLoading={commentsLoading || eventsLoading}
      onCreate={comment => addComment({ comment, objectiveId: objective.id }).unwrap()}
      onDelete={id => deleteComment({ id, objectiveId: objective.id }).unwrap()}
      onUpdate={(id, comment) => patchComment({
        comment,
        id,
        objectiveId: objective.id,
      }).unwrap()}
      updates={(events ?? []).map(event => ({
        createdAt: event.createdAt,
        id: event.id,
        summary: summarizeObjectiveEvent(event, t, {
          projectName: projectId => projects?.find(project => project.id === projectId)?.name,
          userName: userId => {
            const match = users.find(person => person.id === userId);
            return match ? assigneeDisplayName(match) : undefined;
          },
        }),
      }))}
    />
  );
};

export default ObjectiveDiscussion;
